import { useEffect } from 'react';

/***
 * This function restricts input actions to allow:
 * only numbers
 * a single decimal point
 * 2 decimal places
 *
 * It also stops users from pasting data that doesn't match the rules.
 * @param input {HTMLInputElement} - The input element to restrict.
 * @returns {void}
 */
export const useRestrictInputToPrice = (
  input: HTMLInputElement,
  onChange: (event: { target: EventTarget }) => void,
) => {
  const keyDownListener = (e: KeyboardEvent) => {
    const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete', 'Home', 'End', 'Escape'];

    // Allow control/navigation keys
    if (allowedKeys.includes(e.key)) return;
    // Allow Select All (Cmd+A or Ctrl+A)
    if ((e.key === 'a' || e.key === 'A') && (e.metaKey || e.ctrlKey)) return;
    // Allow Copy (Cmd+C or Ctrl+C)
    if ((e.key === 'c' || e.key === 'C') && (e.metaKey || e.ctrlKey)) return;
    // Allow Paste (Cmd+V or Ctrl+V)
    if ((e.key === 'v' || e.key === 'V') && (e.metaKey || e.ctrlKey)) return;

    const isNumberKey = /^[0-9]$/.test(e.key);
    const isDecimal = e.key === '.';

    // Block non-numeric and non-decimal keys
    if (!isNumberKey && !isDecimal) {
      e.preventDefault();
      return;
    }

    const value = input.value;
    const selectionStart = input.selectionStart ?? 0;
    const selectionEnd = input.selectionEnd ?? 0;

    // Prevent multiple decimals
    if (isDecimal && value.includes('.')) {
      e.preventDefault();
      return;
    }

    // Prevent more than 2 decimal places
    if (value.includes('.')) {
      const decimalIndex = value.indexOf('.');
      const decimals = value.substring(decimalIndex + 1);

      // If cursor is after decimal and already 2 decimals, block input
      if (selectionStart > decimalIndex && decimals.length >= 2 && selectionStart === selectionEnd) {
        e.preventDefault();
      }
    }
  };

  const pasteListener = (e: ClipboardEvent) => {
    e.preventDefault();
    if (!e.clipboardData) return;

    const pasteData = e.clipboardData.getData('text');
    const start = input.value.slice(0, input.selectionStart);
    const end = input.value.slice(input.selectionEnd);
    const combinedPasteData = start.concat(pasteData, end);

    // Keep only digits and decimal points
    let sanitized = combinedPasteData.replace(/[^0-9.]/g, '');

    // Allow only one decimal point
    const parts = sanitized.split('.');
    if (parts.length > 2) {
      sanitized = parts[0] + '.' + parts.slice(1).join('');
    }

    // Limit to 2 decimal places
    if (parts.length === 2) {
      sanitized = parts[0] + '.' + parts[1].slice(0, 2);
    }

    if (sanitized.length > 0) {
      input.value = sanitized;
      const newEvent = new Event('paste');
      input.dispatchEvent(newEvent);

      onChange(newEvent);
    }
  };

  useEffect(() => {
    if (input instanceof HTMLInputElement) {
      input.addEventListener('keydown', keyDownListener);
      input.addEventListener('paste', pasteListener);
      const cleanup = () => {
        input.removeEventListener('keydown', keyDownListener);
        input.removeEventListener('paste', pasteListener);
      };
      return cleanup;
    }
  }, [input, onChange]);
};
